import React, { useEffect, useState } from "react";
import { UseFormSetError } from "react-hook-form";
import { z } from "zod";

import { loginPasswordSchema } from "~/screens/Auth/schemas/login.schema";
import { patientScreeningSchema } from "~/services";
import { ErrorText } from "~/ui/common/ErrorText";
import { validateError } from "~/utils";
import { signupSchema } from "../schemas/signup.schema";

type Schemas =
  | z.infer<typeof signupSchema>
  | z.infer<typeof loginPasswordSchema>
  | z.infer<typeof patientScreeningSchema>;

const SHOW_MESSAGE_TIMEOUT = 12_000;

export const useErrorMessage = <T extends Schemas>(
  error: unknown | null,
  setError: UseFormSetError<T>,
) => {
  const [serviceError, setServiceError] = useState<string | string[]>();

  const getErrorMessage = (error: unknown) => {
    const parsedError = validateError(error);

    if (!parsedError) {
      setServiceError(
        "Oops! That didn’t go quite as planned. If it keeps happening, don’t hesitate to reach out — we’ve got you.",
      );

      return;
    }

    const errorMessage = parsedError.response.data.error.message;
    const fields = parsedError.response.data.error.fields;

    if (fields) {
      const errors: Record<string, string>[] = Object.keys(fields)?.map(
        (key) => ({
          field: key,
          message: `${fields[key].join(",\n")}`,
        }),
      );

      errors.forEach(({ field, message }) => {
        setError(field as never, { type: "manual", message });
      });
      return;
    }

    setServiceError(errorMessage);
  };

  const resetErrorMessage = () => setServiceError("");

  const resetErrorMessageOverTime = () =>
    setTimeout(() => {
      resetErrorMessage();
    }, SHOW_MESSAGE_TIMEOUT);

  useEffect(() => {
    if (!error) return;
    getErrorMessage(error);
    resetErrorMessageOverTime();
  }, [error]);

  const ErrorMessage = () =>
    serviceError ? (
      Array.isArray(serviceError) ? (
        <section className="space-y-1">
          {serviceError.map((error, index) => (
            <ErrorText key={index} id={`${error}-error`}>
              {error}
            </ErrorText>
          ))}
        </section>
      ) : (
        <ErrorText id={`${serviceError}-error`}>{serviceError}</ErrorText>
      )
    ) : null;

  return { ErrorMessage, resetErrorMessage };
};
